import { useEffect, useState } from "react";
import { Link } from "react-router";
import type { ScheduleSeriesView } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { seriesCountText } from "../../lib/plurals.ts";
import { Empty } from "../../lib/Empty.tsx";
import { LoadingRows } from "../../lib/loading.tsx";
import { Crumbs } from "../shell/Crumbs.tsx";
import { localDateText, weekdayShort } from "./common.ts";
import { KindMark } from "./EntryKind.tsx";
import { foldSeries, type BunchView } from "./series-bunch.ts";

/**
 * Список серий: договорённости пары, из которых берётся вся неделя.
 *
 * Отдельным экраном от недели намеренно. Неделя отвечает на вопрос «что у меня
 * завтра», а серия на вопрос «как мы договорились», и это разные частоты: в
 * неделю заходят каждый день, в серию несколько раз в год. Смешав их, мы бы
 * положили редкое действие поверх частого экрана.
 *
 * Ученик сюда тоже попадает и видит свои серии: предикат на сервере один
 * («я преподаватель ИЛИ я ученик»), а править он ничего не может, потому что
 * расписание ведёт тот, кто его ведёт.
 *
 * ⚠️ **Группа показывается ОДНОЙ строкой, и сворачивает её клиент** (раздел 3
 * `docs/group-lessons-design.md`, пункт 3). Серии пучка с сервера не убираются
 * намеренно: строка группы собирается из них же, а шесть строк с одинаковыми
 * днями и временем это не список договорённостей, а шум.
 */
export function SeriesListPage() {
  const [series, setSeries] = useState<ScheduleSeriesView[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Один момент времени на весь список: год в датах серий считается от него,
  // им же отбираются действующие серии пучка, и считаться он обязан у всех
  // строк одинаково
  const now = Date.now();
  const shape = series === null ? null : foldSeries(series, now);

  useEffect(() => {
    void (async () => {
      try {
        const res = await api.listSeries();
        setSeries(res.series);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  return (
    <main className="home home--list">
      <header className="head">
        {/* Крошки первой строкой ШАПКИ, а не абзацем над ней: строка «мелкое,
            косая черта, огромное» вышла из того, что крошка и заголовок стояли
            рядом, а не из самой крошки */}
        <Crumbs trail={[{ label: "Расписание", to: "/schedule" }]} here="Серии" />
        <div className="head__line">
          <h1 className="head__h">Серии</h1>
          {/* Считаются СТРОКИ списка, а не серии ответа: занятие группы это N
              серий, свёрнутых в одну строку, и «12 серий» над шестью строками
              было бы прямой неправдой */}
          {shape !== null && (
            <span className="head__n">
              {seriesCountText(shape.pairs.length + shape.groups.length)}
            </span>
          )}
          {/*
            Главное действие экрана, и единственный акцент на нём. У строк серий
            акцента нет намеренно: их на экране десяток, и залитая «Править»
            в каждой строке означала бы ноль выделенных кнопок
          */}
          <span className="head__act">
            <Link className="btn btn--primary" to="/schedule/series/new">
              Новая серия
            </Link>
          </span>
        </div>
      </header>

      {err !== null && <p className="err">{err}</p>}

      <section className="card">
        {shape === null ? (
          <LoadingRows list="list--series" cells={5} />
        ) : shape.pairs.length === 0 && shape.groups.length === 0 ? (
          <Empty
            icon="calendar"
            title="Серий пока нет"
            text="Серия это повторяющаяся договорённость с одним учеником: вторник в 16:00 и четверг в 18:30 это одна серия, а не две."
            action={{ label: "Новая серия", to: "/schedule/series/new" }}
          />
        ) : (
          <>
            {/*
              Пар нет, а группы есть: списка пар тут не будет вовсе, и пустого
              состояния под ним тоже. «Серий пока нет» под списком групп было бы
              неправдой, потому что строка группы это и есть серии, свёрнутые в
              одну. Текст остаётся тем же и на прежнем месте: его читает тот, у
              кого нет ни одной серии никакого вида
            */}
            {shape.pairs.length > 0 && (
              <>
                {/*
                  Заголовки появляются вместе с группами, и это не экономия
                  разметки. Преподаватель без групп видит ровно то же, что видел
                  вчера: одну таблицу без единого лишнего слова. Деление на
                  разделы существует ровно тогда, когда есть что делить, а слова
                  взяты у того же выбора в диалоге создания занятия («Ученики» и
                  «Группы»): два названия для одной развилки читались бы как две
                  разные развилки
                */}
                {shape.groups.length > 0 && <h2>Ученики</h2>}
                <ul className="list list--series">
                  <li className="list__head" aria-hidden="true">
                    <span>Ученик</span>
                    <span>Дни и время</span>
                    <span>Длительность</span>
                    <span>Период</span>
                    <span>Действие</span>
                  </li>
                  {shape.pairs.map((s) => (
                    <SeriesRow key={s.id} series={s} now={now} />
                  ))}
                </ul>
              </>
            )}

            {shape.groups.length > 0 && (
              <>
                <h2>Группы</h2>
                {/*
                  Шаблон колонок ТОТ ЖЕ, что у пар (`list--series`), и второго
                  заводить не надо: вопросов к строке те же пять, а общий шаблон
                  ставит обе таблицы в одну сетку, то есть глаз читает их
                  подряд, не перестраиваясь
                */}
                <ul className="list list--series">
                  <li className="list__head" aria-hidden="true">
                    <span>Группа</span>
                    <span>Дни и время</span>
                    <span>Длительность</span>
                    <span>Период</span>
                    <span>Действие</span>
                  </li>
                  {shape.groups.map((b) => (
                    <BunchRow key={b.groupId} bunch={b} now={now} />
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </section>
    </main>
  );
}

function SeriesRow(props: { series: ScheduleSeriesView; now: number }) {
  const s = props.series;

  return (
    // Отвязанная пара приглушена, но читаема: новых занятий по ней не будет, а
    // прошлые договорённости остаются частью истории
    <li className="row" data-dim={s.unlinked ? "true" : undefined}>
      {/* Имени может не быть у ученика, который пришёл по ссылке и ещё не
          представился. Слот при этом занят словами, а не пустотой: пустой он
          схлопнется, и колонки соседних строк разъедутся */}
      <span
        className={`cell row__name${s.counterpartName === null ? " row__void" : ""}`}
      >
        {s.counterpartName ?? "без имени"}
        {s.unlinked && <span className="row__sub">пара отвязана</span>}
      </span>

      {/*
        Дни серии одной строкой, и часовой пояс подсказкой к ним же. Пояс стоял
        в одной подписи с длительностью, то есть в чужой колонке: это и есть та
        каша, от которой уходим. Своей колонки он не заслуживает, потому что у
        всех серий преподавателя он один и тот же
      */}
      <span
        className="cell row__when row__when--wrap"
        data-label="Дни и время"
        title={`Часовой пояс: ${s.timeZone}`}
      >
        {slotsText(s)}
      </span>

      <span className="cell row__num" data-label="Длительность">
        {s.durationMin} мин
      </span>

      <span className="cell" data-label="Период">
        {periodText(s.startsOn, s.endsOn, props.now)}
      </span>

      <span className="cell row__act">
        <Link className="btn btn--sm" to={`/schedule/series/${s.id}`}>
          {s.myRole === "teacher" ? "Править" : "Открыть"}
        </Link>
      </span>
    </li>
  );
}

/** Строка группы: те же пять слотов, что у пары, и все заняты всегда. */
function BunchRow(props: { bunch: BunchView; now: number }) {
  const b = props.bunch;
  /**
   * Действующих серий у пучка не осталось: расписание кончилось.
   *
   * ⚠️ Строка при этом остаётся в списке (разбор в `series-bunch.ts`), и сказать
   * это ей приходится СЛОВАМИ. Пометки «(закрыт)» у дней не будет: роспуск
   * пишет окончание серии, а не слота, поэтому дни выглядят ровно как у живого
   * расписания, а колонка периода читается только вместе с сегодняшним числом.
   */
  const ended = b.size === null;

  return (
    // Приглушена ровно как отвязанная пара, и по той же причине: занятий впереди
    // нет, а договорённость была и остаётся частью истории
    <li className="row" data-dim={ended ? "true" : undefined}>
      {/* Название группы стоит там же, где у пары стоит имя ученика: вопрос у
          слота один, «с кем эти занятия» */}
      <span className={`cell row__name${b.title === null ? " row__void" : ""}`}>
        {/*
          Сколько человек ходит, значком с числом, а не словом (решение
          владельца 30.08.2026, правило целиком в разделе 8б
          `docs/cabinet-tables-design.md`). Отдельной колонки под это нет: строка
          отвечает на те же пять вопросов, что строка пары, и шестая колонка
          съела бы ширину у дней и времени, ради которых в список и смотрят.

          ⚠️ **Фигуру рисует тот же компонент, что и в сетке** (`KindMark`).
          Своей копии здесь стояло до 30.08.2026, и разъехавшись, копии дали бы
          читалке разные слова об одной и той же фигуре.
        */}
        <KindMark kind="group" size={b.size} />
        {/* Названия без имени у группы не бывает, но слот обязан быть занят
            словами при любом ответе сервера: пустой он схлопнется, и колонки
            соседних строк разъедутся */}
        {b.title ?? "без имени"}
        {ended && <span className="row__sub">расписание кончилось</span>}
      </span>

      {/* Дни у сестёр общие: правит их массовая ручка пучка, поодиночке слот
          группы не двигается. Пояс подсказкой к ним же, как у пары */}
      <span
        className="cell row__when row__when--wrap"
        data-label="Дни и время"
        title={`Часовой пояс: ${b.base.timeZone}`}
      >
        {slotsText(b.base)}
      </span>

      <span className="cell row__num" data-label="Длительность">
        {b.base.durationMin} мин
      </span>

      <span className="cell" data-label="Период">
        {periodText(b.startsOn, b.endsOn, props.now)}
      </span>

      <span className="cell row__act">
        {/* Своим экраном, а не серией одного из участников: у пучка общие поля,
            общие дни и общий состав, и правятся они разом.

            ⚠️ У кончившегося расписания подпись другая, и это не мелочь: править
            там нечего, экран группы в этом состоянии предлагает завести новое.
            «Править» обещало бы то, чего на экране не будет */}
        <Link className="btn btn--sm" to={`/schedule/groups/${b.groupId}`}>
          {ended ? "Открыть" : "Править"}
        </Link>
      </span>
    </li>
  );
}

/**
 * Период занятий словами: «с 3 мар, бессрочно» либо «с 3 мар по 25 дек».
 *
 * Одной функцией на обе секции: у пары и у пучка это один и тот же слот с одним
 * и тем же вопросом, а две копии фразы разошлись бы в первую же правку.
 */
function periodText(startsOn: string, endsOn: string | null, now: number): string {
  const from = `с ${localDateText(startsOn, now)}`;
  return endsOn === null ? `${from}, бессрочно` : `${from} по ${localDateText(endsOn, now)}`;
}

/**
 * Дни серии одной строкой: «вт 16:00, чт 18:30».
 *
 * Это и есть та самая «разное время в разные дни», и она обязана читаться с
 * одного взгляда: ради неё серия и не разложена на два независимых правила.
 */
function slotsText(series: ScheduleSeriesView): string {
  if (series.slots.length === 0) return "дней нет";
  return series.slots
    .map((s) => `${weekdayShort(s.weekday)} ${s.startsAt}${s.endsOn === null ? "" : " (закрыт)"}`)
    .join(", ");
}
